import React from "react";
import { Box, IconButton, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import StopIcon from "@mui/icons-material/Stop";
import { useDispatch } from "react-redux";
import { i18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";

import { tallyTokens } from "../../theme.tsx";
import { voiceActions } from "../../store/voice.tsx";
import {
  interruptVoice,
  isVoiceBusy,
  useVoiceStatus,
} from "../../voice/voiceStatus.ts";

const roundButtonSx = {
  m: 0,
  p: 0,
  flexShrink: 0,
  width: "31px",
  height: "31px",
  borderRadius: "999px",
};

/**
 * A running voice session's controls: Stop while Tally thinks or speaks, and
 * End, which leaves voice mode. Shown in place of the composer's send button,
 * or floating on their own when no composer is on screen.
 */
const VoiceControls: React.FC = () => {
  const dispatch = useDispatch();
  const state = useVoiceStatus((status) => status.state);

  const endLabel = i18n._(
    msg({
      message: "End voice mode",
      context: "Voice mode control that leaves the session",
    }),
  );
  // Stop means different things: while Tally speaks it only silences the
  // voice (the written answer keeps streaming); while it thinks it cancels the
  // question, since there is nothing to silence yet.
  const stopLabel =
    state === "speaking"
      ? i18n._(
          msg({
            message: "Stop speaking",
            context: "Voice mode control to interrupt playback",
          }),
        )
      : i18n._(
          msg({
            message: "Cancel this question",
            context:
              "Voice mode control that cancels a question Tally is still working on",
          }),
        );

  return (
    <Box
      sx={{ display: "flex", gap: "6px", marginLeft: "auto", flexShrink: 0 }}
    >
      {isVoiceBusy(state) && (
        <Tooltip title={stopLabel}>
          <IconButton
            aria-label="Stop"
            onClick={interruptVoice}
            sx={{
              ...roundButtonSx,
              bgcolor: tallyTokens.surface2,
              color: tallyTokens.ink,
              "&:hover": {
                bgcolor: tallyTokens.surface2,
                filter: "brightness(0.96)",
              },
            }}
          >
            <StopIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      <Tooltip title={endLabel}>
        <IconButton
          aria-label={endLabel}
          onClick={() => dispatch(voiceActions.exitVoiceMode())}
          sx={{
            ...roundButtonSx,
            bgcolor: tallyTokens.brand,
            color: "#fff",
            "&:hover": {
              bgcolor: tallyTokens.brand,
              filter: "brightness(1.07)",
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default VoiceControls;
